"use client";
import { useState } from "react";
import { CalendarDaysIcon, CheckCircle2Icon, ClockIcon } from "lucide-react";
import { Button } from "@/components/ui/button";

const daysList = [
  { short: "Seg", full: "Segunda", date: 12 },
  { short: "Ter", full: "Terça", date: 13 },
  { short: "Qua", full: "Quarta", date: 14 },
  { short: "Qui", full: "Quinta", date: 15 },
  { short: "Sex", full: "Sexta", date: 16 },
];

export function InteractiveBookingWidget() {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<typeof daysList[0] | null>(null);

  const times = ["09:00", "10:30", "14:00", "15:30", "17:00"];

  return (
    <div className="relative w-full max-w-sm mx-auto group perspective-1000">
      <div className="absolute -inset-1 bg-gradient-to-r from-primary/50 to-purple-600/50 rounded-3xl blur-xl opacity-50 group-hover:opacity-100 transition duration-700"></div>
      
      <div className="relative bg-[#0a0a0a]/90 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 shadow-2xl transition-transform duration-500 hover:rotate-x-2 hover:-rotate-y-2">
        
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="size-12 rounded-full bg-gradient-to-br from-primary to-purple-600 p-0.5">
             <div className="w-full h-full bg-[#0a0a0a] rounded-full flex items-center justify-center">
               <span className="font-bold text-white">S</span>
             </div>
          </div>
          <div>
            <h3 className="font-bold text-white text-lg">Studio Premium</h3>
            <p className="text-xs text-white/50">Corte & Barba • 60 min</p>
          </div>
        </div>

        {/* Dynamic Content based on Step */}
        <div className="min-h-[220px]">
          {step === 1 && (
            <div className="space-y-4 animate-in fade-in zoom-in-95 duration-500">
              <p className="text-sm font-medium text-white/80 flex items-center gap-2">
                <CalendarDaysIcon className="size-4" /> Escolha o dia
              </p>
              <div className="grid grid-cols-5 gap-2">
                {daysList.map((day, i) => (
                  <button 
                    key={day.short} 
                    onClick={() => {
                      setSelectedDay(day);
                      setStep(2);
                    }}
                    className={`p-3 rounded-xl border flex flex-col items-center justify-center transition-all ${selectedDay?.short === day.short || (i === 2 && !selectedDay) ? 'bg-primary/20 border-primary text-primary' : 'bg-white/5 border-white/5 text-white/60 hover:bg-white/10'}`}
                  >
                    <span className="text-[10px] uppercase mb-1">{day.short}</span>
                    <span className="font-bold">{day.date}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-500">
              <button onClick={() => setStep(1)} className="text-xs text-primary hover:underline mb-2 block">← Voltar</button>
              <p className="text-sm font-medium text-white/80 flex items-center gap-2">
                <ClockIcon className="size-4" /> Horários disponíveis
              </p>
              <div className="grid grid-cols-3 gap-2">
                {times.map((time) => (
                  <button 
                    key={time}
                    onClick={() => setSelectedTime(time)}
                    className={`py-2 rounded-lg text-sm font-medium transition-all border ${selectedTime === time ? 'bg-primary border-primary text-white scale-105 shadow-lg shadow-primary/30' : 'bg-white/5 border-white/5 text-white/70 hover:bg-white/10'}`}
                  >
                    {time}
                  </button>
                ))}
              </div>
              <Button 
                onClick={() => setStep(3)} 
                disabled={!selectedTime}
                className="w-full mt-4 bg-white text-black hover:bg-white/90 font-bold rounded-xl h-12"
              >
                Continuar
              </Button>
            </div>
          )}

          {step === 3 && (
            <div className="flex flex-col items-center justify-center h-[200px] space-y-4 animate-in fade-in zoom-in-95 duration-500 text-center">
              <div className="size-16 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center mb-2 animate-bounce [animation-duration:2s]">
                 <CheckCircle2Icon className="size-8" />
              </div>
              <h4 className="font-bold text-white text-xl">Agendado!</h4>
              <p className="text-sm text-white/50">
                {selectedDay?.full || "Quarta"}, {selectedDay?.date || "14"} às {selectedTime}
              </p>
              <button onClick={() => { setStep(1); setSelectedTime(null); setSelectedDay(null); }} className="text-xs text-white/30 hover:text-white mt-4 underline">
                Testar novamente
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Floating indicator */}
      <div className="absolute -right-12 top-10 bg-black/80 backdrop-blur-md border border-white/10 px-4 py-2 rounded-full text-xs font-bold text-white shadow-xl animate-bounce [animation-duration:3s]">
        👆 Interaja comigo!
      </div>
    </div>
  );
}
